import type { LeadCatalogResponse, SmartListDetail, SmartListRevision } from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { useMemo, type JSX, type ReactNode } from "react";
import { Button } from "../design/components/Button";
import "../design/components/data-table.css";
import { Dialog } from "../design/components/Dialog";
import { Reason } from "../design/components/Reason";
import { messageForError } from "../i18n/errors";
import { formatNumericDateTime } from "../i18n/format";
import { useT } from "../i18n/locale";
import type { ColumnDef } from "../leads/columns/table-layout";
import type { TabContext } from "../session/tab-context";
import { useChangeFields } from "./SmartListDialogs";
import { smartListRevisionsQuery } from "./smart-list-queries";
import { conditionsInChanges, describeChanges } from "./smart-list-text";
import "./smart-lists.css";

// «Storico della lista» (piano M3 Task 9, D8; mock-up `openHistory`): le versioni dalla più recente, ognuna con il numero,
// quando, chi e che cosa è cambiato in parole semplici (`describeChanges`, dalle differenze che il server ha scritto).
// La versione in uso lo dice; le altre hanno «Rimetti questa versione», che riporta filtri e ordinamento di allora come
// bozza non salvata (decisione P6): niente si scrive finché non si preme «Salva», e il salvataggio scrive le differenze
// normali. Le versioni arrivano già senza le condizioni sui campi che non ci sono più (decisione P27).

type Props = {
  tab: TabContext;
  list: SmartListDetail;
  catalog: LeadCatalogResponse | undefined;
  columns: readonly ColumnDef[];
  timeZone: string;
  onRestore: (revision: SmartListRevision) => void;
  onClose: () => void;
};

export function HistoryDialog({ tab, list, catalog, columns, timeZone, onRestore, onClose }: Props): JSX.Element {
  const t = useT();
  const revisions = useQuery(smartListRevisionsQuery(tab, list.id));
  const conditions = useMemo(() => (revisions.data ?? []).flatMap((revision) => conditionsInChanges(revision.changes)), [revisions.data]);
  const fields = useChangeFields({ tab, catalog, columns, conditions });

  let content: ReactNode;
  if (revisions.isError && !revisions.data) {
    content = (
      <div className="g-smart-page__problem">
        <Reason tone="warning" role="alert">
          {messageForError(t, revisions.error)}
        </Reason>
        <Button onClick={() => void revisions.refetch()}>{t("common.retry")}</Button>
      </div>
    );
  } else if (!revisions.data) {
    content = <p>{t("common.loading")}</p>;
  } else {
    content = (
      <div className="g-table-wrap">
        <table className="g-table g-smart-history">
          <caption className="g-visually-hidden">{t("smartLists.page.historyCaption")}</caption>
          <thead>
            <tr>
              <th scope="col">{t("smartLists.page.historyVersion")}</th>
              <th scope="col">{t("smartLists.page.historyWhen")}</th>
              <th scope="col">{t("smartLists.page.historyWho")}</th>
              <th scope="col">{t("smartLists.page.historyWhat")}</th>
              <th scope="col">
                <span className="g-visually-hidden">{t("smartLists.page.historyAction")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {revisions.data.map((revision) => (
              <tr key={revision.revision}>
                <td className="g-smart-history__number">{revision.revision}</td>
                <td>{formatNumericDateTime(timeZone, revision.savedAt)}</td>
                <td>{revision.savedBy.displayName}</td>
                <td className="g-smart-history__what">
                  {describeChanges(t, revision.changes, fields).map((line, index) => (
                    <span key={`${index}-${line}`}>{line}</span>
                  ))}
                </td>
                <td>
                  {revision.revision === list.revision ? (
                    <span className="g-smart-history__current">{t("smartLists.page.inUse")}</span>
                  ) : (
                    <Button onClick={() => onRestore(revision)}>{t("smartLists.page.restore")}</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <Dialog
      open
      size="wide"
      title={t("smartLists.page.history")}
      onClose={onClose}
      footer={
        <>
          <p className="g-new-list__note g-smart-history__note">{t("smartLists.page.historyNote")}</p>
          <Button onClick={onClose}>{t("common.close")}</Button>
        </>
      }
    >
      {content}
    </Dialog>
  );
}
